import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import {
  HTTP_INTERCEPTORS,
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { Injectable, Injector, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalRedirectComponent,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { DirectoryService } from './services/directory.service';
import { XWareLogService } from './services/xware-log.service';
import { TokenInterceptor } from './interceptors/token.interceptor';
import { IDIRECTORYSERVICE } from './services/idirectory.service';
import { MeSignatureComponent } from './core/me/me-signature/me-signature.component';
import { MeAttributesComponent } from './core/me/me-attributes/me-attributes.component';
import { DlgOkCancelComponent } from './components/dlg-ok-cancel/dlg-ok-cancel.component';
import { ErrorPageLoadingComponent } from './components/error-page-loading/error-page-loading.component';
import { LimitStringPipe } from './pipes/limitString.pipe';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { environment } from '../environments/environment';
import { AngularMaterialModule } from './angular.material.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthModule } from './auth/auth.module';
import { ParameterService } from './services/parameter.service';
import { MsalComponent } from './MsalComponent';
import { CwCommonModule } from './modules/cw-common.module';
import { IAUTHSERVICE } from './services/iauth.service';
import { CWGapiInterceptor } from './auth/gapi/cw-gapi-interceptor';
import { CwGapiService } from './services/cw-gapi.service';
import { CwMsalService } from './services/cw-msal.service';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { CoreModule } from './core/core.module';

const isIE =
  window.navigator.userAgent.indexOf('MSIE') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;
let _ADType = sessionStorage.getItem('adType');

export function loggerCallback(logLevel, message, piiEnabled) {}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msal_clientID,
      authority: 'https://login.microsoftonline.com/common/',
      redirectUri: environment.redirectUri,
      postLogoutRedirectUri: environment.postLogoutRedirectUri,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11
    },
    system: {
      loggerOptions: {
        loggerCallback: loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.portalapiurl, [environment.msal_ScopesAPI]);
  protectedResourceMap.set(environment.apiSignaturePreviewServer, [environment.msal_ScopesAPI]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    authRequest: {
      scopes: [environment.msal_ScopesAPI],
      extraQueryParameters: { prompt: 'select_account' },
    },
    interactionType: InteractionType.Redirect,
  };
}

export function authServiceFactory(injector: Injector) {
  if (!_ADType) {
    _ADType = sessionStorage.getItem('adType');
  }
  if (_ADType === 'gapi') {
    return new CwGapiService(injector.get(XWareLogService), injector.get(Router));
  } else {
    return new CwMsalService(
      MSALGuardConfigFactory(),
      injector.get(Router),
      injector.get(MsalService),
      injector.get(XWareLogService),
      injector.get(MsalBroadcastService)
    );
  }
}

@Injectable()
export class CustomInterceptor implements HttpInterceptor {
  private gapiInterceptor: CWGapiInterceptor;

  constructor(private injector: Injector) {
    const cwGapiService = this.injector.get(CwGapiService);
    this.gapiInterceptor = new CWGapiInterceptor(cwGapiService);
  }
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const _ADType = sessionStorage.getItem('adType');
    if (_ADType === 'gapi') {
      return this.gapiInterceptor.intercept(req, next);
    } else {
      return this.injector.get(MsalInterceptor).intercept(req, next);
    }
  }
}

@NgModule({
  declarations: [
    MeAttributesComponent,
    MeSignatureComponent,
    AppComponent,
    MsalComponent,
    DlgOkCancelComponent,
    ErrorPageLoadingComponent,
    LimitStringPipe,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    AngularMaterialModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    AuthModule,
    CoreModule,
    AppRoutingModule,
    MsalModule,
    CwCommonModule,
  ],
  providers: [
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: IDIRECTORYSERVICE,
      useExisting: DirectoryService,
    },
    {
      provide: IAUTHSERVICE,
      useFactory: authServiceFactory,
      deps: [Injector],
    },
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomInterceptor,
      deps: [Injector],
      multi: true,
    },
    MsalInterceptor,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    ParameterService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor(parameterService: ParameterService) {
    if (window === window.parent || window.opener) {
      // if already captured, don't wipe them
      if (!sessionStorage.getItem('initParams')) {
        parameterService.getInitialParameters();
      }
    }

    if (location.search !== '') {
      const objParam = this.doParams(location.search);
      sessionStorage.setItem('initParams', JSON.stringify(objParam));
    }
  }

  doParams(strSearch) {
    const objParams = strSearch
      .slice(1)
      .split('&')
      .map(p => p.split('='))
      .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});
    return objParams;
  }
}
